import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../lib/anim";
import { PORTFOLIO_CATEGORIES, DEMO_PORTFOLIO, CATEGORIES } from "../data/content";
import { useMedia } from "../context/MediaContext";
import { Play, X, ArrowRight } from "lucide-react";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// Rang de chaque catégorie pour le regroupement de l'onglet « Tous »
const CATEGORY_RANK = CATEGORIES.reduce((acc, name, i) => ({ ...acc, [name]: i }), {});
const catRank = (c) => (c in CATEGORY_RANK ? CATEGORY_RANK[c] : CATEGORIES.length);

const Portfolio = () => {
  const { portfolio } = useMedia();
  const [active, setActive] = useState("Tous");
  const [selected, setSelected] = useState(null);

  const allItems = useMemo(() => {
    if (portfolio.length > 0) {
      return portfolio.map((m) => ({
        id: m.id, title: m.title || "Réalisation", category: m.category || "Autres projets",
        // `poster` DOIT figurer ici : cette projection reconstruit l'objet champ par champ,
        // donc toute clé omise est perdue. Sans elle, l'attribut poster de la balise vidéo
        // vaut undefined et la carte reste noire, même quand la miniature existe bien.
        poster: m.poster || "",
        url: m.url, kind: m.kind, isDemo: false,
      }));
    }
    return DEMO_PORTFOLIO.map((d) => ({ ...d, url: d.img, kind: "image", isDemo: true }));
  }, [portfolio]);

  const items = useMemo(() => {
    if (active !== "Tous") return allItems.filter((p) => p.category === active);
    // Onglet « Tous » : regroupement par catégorie (ordre défini), tri stable conservant l'ordre interne
    return allItems
      .map((item, idx) => ({ item, idx }))
      .sort((a, b) => catRank(a.item.category) - catRank(b.item.category) || a.idx - b.idx)
      .map(({ item }) => item);
  }, [active, allItems]);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 lg:py-40 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-8 max-w-2xl">
          <Reveal><p className="eyebrow mb-6">Portfolio / Réalisations</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F5F5F0]">
              Une galerie <span className="italic gold-text">immersive</span>.
            </h2>
          </Reveal>
          {allItems[0]?.isDemo && (
            <Reveal delay={0.15}>
              <p data-testid="portfolio-demo-note" className="mt-5 text-sm text-[#A1A1AA] border-l-2 border-[#D4AF37]/50 pl-4">
                Aperçus de démonstration — vos véritables réalisations remplaceront ces médias dès leur mise en ligne.
              </p>
            </Reveal>
          )}
        </div>

        <Reveal>
          <div className="flex flex-wrap gap-3 mb-12">
            {PORTFOLIO_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setActive(c)}
                className={`rounded-full px-5 py-2 text-sm tracking-wide border transition-colors duration-300 ${
                  active === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#F5F5F0]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`portfolio-item-${p.id}`}
                data-cursor-hover
                onClick={() => setSelected(p)}
                className="group relative h-[300px] lg:h-[340px] overflow-hidden rounded-sm cursor-pointer bg-[#0d0d0d]"
              >
                {p.kind === "video" ? (
                  <video className="absolute inset-0 w-full h-full object-cover" muted playsInline preload="none" poster={p.poster || undefined}>
                    <source src={`${p.url}#t=0.1`} type="video/mp4" />
                  </video>
                ) : (
                  <img src={p.url} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[900ms]" />
                )}
                <div className="absolute inset-0 bg-black/45 group-hover:bg-black/25 transition-colors duration-500" />
                {p.isDemo && (
                  <span className="absolute top-4 left-4 z-10 text-[9px] tracking-[0.2em] uppercase text-black bg-[#D4AF37] rounded-full px-2.5 py-1">Démo</span>
                )}
                <div className="absolute inset-0 grid place-items-center">
                  <span className="grid place-items-center w-16 h-16 rounded-full border border-white/40 text-white group-hover:border-[#D4AF37] group-hover:text-[#D4AF37] group-hover:scale-110 transition-all duration-500 backdrop-blur-sm">
                    <Play size={20} className="ml-0.5" />
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="eyebrow text-[9px] text-[#D4AF37]">{p.category}</span>
                  <h3 className="font-serif text-2xl text-[#F5F5F0] mt-1">{p.title}</h3>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {items.length === 0 && (
          <p data-testid="portfolio-empty" className="text-[#A1A1AA] py-16 text-center">Aucune réalisation dans cette catégorie pour le moment.</p>
        )}

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <p className="font-serif italic text-2xl text-[#F5F5F0]">Un projet en tête&nbsp;?</p>
            <button
              data-testid="portfolio-cta"
              onClick={() => scrollTo("devis")}
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black rounded-full pl-6 pr-2 py-2 text-sm font-medium tracking-wide hover:bg-[#F3E19B] transition-colors duration-300"
            >
              Lancer votre projet
              <span className="grid place-items-center w-9 h-9 rounded-full bg-black text-[#D4AF37] group-hover:translate-x-1 transition-transform duration-300">
                <ArrowRight size={16} />
              </span>
            </button>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="portfolio-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-md grid place-items-center p-6"
          >
            <button data-testid="lightbox-close" onClick={() => setSelected(null)} className="absolute top-6 right-6 text-white hover:text-[#D4AF37] transition-colors">
              <X size={30} />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl aspect-video overflow-hidden rounded-sm border border-white/10 bg-black"
            >
              {selected.kind === "video" ? (
                <video className="w-full h-full object-contain bg-black" controls autoPlay playsInline poster={selected.poster || undefined}>
                  <source src={selected.url} type="video/mp4" />
                </video>
              ) : (
                <>
                  <img src={selected.url} alt={selected.title} className="w-full h-full object-cover" />
                  {selected.isDemo && (
                    <div className="absolute inset-0 bg-black/30 grid place-items-center text-center">
                      <div>
                        <span className="grid place-items-center w-20 h-20 rounded-full border border-[#D4AF37] text-[#D4AF37] mx-auto"><Play size={26} className="ml-1" /></span>
                        <p className="text-[#A1A1AA] mt-4 text-sm">Aperçu de démonstration</p>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="absolute bottom-0 left-0 p-6 pointer-events-none">
                <span className="eyebrow text-[9px] text-[#D4AF37]">{selected.category}</span>
                <h3 className="font-serif text-3xl text-white mt-1">{selected.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
